import React from 'react';

import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';

import { IntegrationUsageLogFormatEnum, IntegrationUsageLogWhere } from 'src/api';
import { Modal } from 'src/components/common';
import { INTEGRATION_USAGE_LOG_EXPORT_COLUMNS } from 'src/constants/integrationUsageLog';
import { useExportIntegrationUsageLogs } from 'src/hooks/integration';
import { useModalV2 } from 'src/hooks/shared';
import { ModalV2Props } from 'src/types/modalV2';

export type ExportFilterSummaryItem = {
  label: string;
  value: string;
};

type ExportIntegrationUsageLogsModalProps = {
  where: IntegrationUsageLogWhere;
  filterSummary: ExportFilterSummaryItem[];
  integrationName: string | undefined;
};

const FORMAT_OPTIONS: { value: IntegrationUsageLogFormatEnum; label: string }[] = [
  { value: IntegrationUsageLogFormatEnum.Csv, label: 'CSV (.csv)' },
  { value: IntegrationUsageLogFormatEnum.Xlsx, label: 'Excel (.xlsx)' },
  { value: IntegrationUsageLogFormatEnum.Json, label: 'JSON (.json)' },
];

export const ExportIntegrationUsageLogsModal: React.FC<
  ModalV2Props<ExportIntegrationUsageLogsModalProps>
> = ({ id, where, filterSummary, integrationName }) => {
  const { closeModal, switchClosability } = useModalV2();
  const { exportUsageLogs, exporting } = useExportIntegrationUsageLogs();

  const [selectedKeys, setSelectedKeys] = React.useState<string[]>(
    INTEGRATION_USAGE_LOG_EXPORT_COLUMNS.map(column => column.key),
  );
  const [format, setFormat] = React.useState<IntegrationUsageLogFormatEnum>(
    IntegrationUsageLogFormatEnum.Csv,
  );

  const onClose = React.useCallback(() => {
    closeModal(id);
  }, [closeModal, id]);

  const toggleColumn = React.useCallback((key: string) => {
    setSelectedKeys(previous =>
      previous.includes(key) ? previous.filter(item => item !== key) : [...previous, key],
    );
  }, []);

  const onExport = React.useCallback(async () => {
    switchClosability(false);
    try {
      // Preserve the registry order regardless of toggle order.
      const orderedKeys = INTEGRATION_USAGE_LOG_EXPORT_COLUMNS.filter(column =>
        selectedKeys.includes(column.key),
      ).map(column => column.key);

      const succeeded = await exportUsageLogs(integrationName, where, orderedKeys, format);
      if (succeeded) {
        onClose();
      }
    } finally {
      switchClosability(true);
    }
  }, [exportUsageLogs, format, onClose, selectedKeys, switchClosability, where, integrationName]);

  const closeButton = React.useMemo(
    () => ({
      show: true,
      actionName: 'close',
      onAction: onClose,
      render: () => (
        <IconButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      ),
    }),
    [onClose],
  );

  return (
    <Modal
      title="Export usage logs"
      topAction={closeButton}
      maxWidth={520}
      dialogProps={{ open: true, onClose }}
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            Applied filters
          </Typography>
          <Stack spacing={0.5} mt={0.5}>
            {filterSummary.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No filters applied — all requests will be exported.
              </Typography>
            ) : (
              filterSummary.map(item => (
                <Typography key={item.label} variant="body2">
                  <Box component="span" color="text.secondary">
                    {item.label}:
                  </Box>{' '}
                  {item.value}
                </Typography>
              ))
            )}
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography variant="overline" color="text.secondary">
            Columns
          </Typography>
          <Box
            mt={0.5}
            display="grid"
            gridTemplateColumns="repeat(2, minmax(0, 1fr))"
            columnGap={2}
            rowGap={0.5}
          >
            {INTEGRATION_USAGE_LOG_EXPORT_COLUMNS.map(column => (
              <FormControlLabel
                key={column.key}
                control={
                  <Checkbox
                    checked={selectedKeys.includes(column.key)}
                    onChange={() => toggleColumn(column.key)}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    {column.label}
                  </Box>
                }
              />
            ))}
          </Box>
        </Box>

        <Divider />

        <FormControl>
          <FormLabel>
            <Typography variant="overline" color="text.secondary">
              Format
            </Typography>
          </FormLabel>
          <RadioGroup
            row
            value={format}
            onChange={event => setFormat(event.target.value as IntegrationUsageLogFormatEnum)}
          >
            {FORMAT_OPTIONS.map(option => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <Box display="flex" gap={2} justifyContent="space-between" alignItems="center">
          <Button variant="text" onClick={onClose} disabled={exporting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onExport}
            disabled={selectedKeys.length === 0 || exporting}
            loading={exporting}
          >
            Export
          </Button>
        </Box>
      </Stack>
    </Modal>
  );
};
